import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';
import { useWhatsAppSupport } from '../hooks/useWhatsAppSupport';

export default function WhatsAppFloatingButton() {
  const [user, setUser] = useState(null);
  const [waConfig, setWaConfig] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [pwaPromptOpen, setPwaPromptOpen] = useState(false);
  const location = useLocation();

  // When the PWA install prompt is on-screen, collapse this FAB to the small
  // icon so the two mobile popups never overlap.
  useEffect(() => {
    const h = (e) => setPwaPromptOpen(Boolean(e.detail?.open));
    window.addEventListener('osh:pwa-prompt', h);
    return () => window.removeEventListener('osh:pwa-prompt', h);
  }, []);

  // Broadcast our expanded state so the PWA install prompt can hide itself
  // when the user opens the WhatsApp support popup.
  useEffect(() => {
    const expanded = !minimized && !pwaPromptOpen;
    window.dispatchEvent(new CustomEvent('osh:wa-expanded', { detail: { open: expanded } }));
  }, [minimized, pwaPromptOpen]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/settings/public`);
        const wa = res.data?.whatsapp_support;
        if (wa?.enabled && wa?.phone_number) setWaConfig(wa);
        else setWaConfig(null);
      } catch { /* silent */ }
    })();
  }, []);

  // Re-fetch user on route change so logged-in state is always current
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch { setUser(null); }
    })();
  }, [location.pathname]);

  const { openSupport, isAvailable } = useWhatsAppSupport(waConfig, user);

  // Hide on conversation pages — the FAB sits in the bottom-right corner
  // exactly where the chat send button / offer modal CTAs live, so on
  // mobile it overlaps the controls. Operators can still reach support
  // from the footer link.
  const HIDE_ON_PATHS = ['/messaging', '/messages', '/chat', '/offer'];
  const shouldHide = HIDE_ON_PATHS.some(p => location.pathname.startsWith(p));

  if (!isAvailable || !waConfig?.floating_button_enabled || shouldHide) return null;

  // Force collapse to the small icon while the PWA install prompt is visible.
  if (minimized || pwaPromptOpen) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-[60] w-12 h-12 rounded-full bg-[#25D366] text-white shadow-lg hover:bg-[#20BD5A] transition-all flex items-center justify-center"
        data-testid="whatsapp-fab-minimized"
        aria-label="Open WhatsApp support"
      >
        <MessageCircle className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2" data-testid="whatsapp-fab">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-3 max-w-[220px] animate-in fade-in slide-in-from-bottom-2">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-xs font-semibold text-slate-800 leading-tight">Need help?</p>
          <button
            onClick={() => setMinimized(true)}
            className="text-slate-400 hover:text-slate-600 -mt-0.5"
            data-testid="whatsapp-fab-minimize"
            aria-label="Minimize"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mb-2.5 leading-snug">Message OnSiteHand support on WhatsApp</p>
        <button
          onClick={openSupport}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#25D366] text-white text-xs font-semibold hover:bg-[#20BD5A] transition-colors"
          data-testid="whatsapp-fab-open"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp Support
        </button>
      </div>
    </div>
  );
}
